import { skipToken } from '@reduxjs/toolkit/query';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useLocalStorage, useSessionStorage } from 'usehooks-ts';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import {
  useGetApiNotesByVersionIdQuery,
  useGetApiArticlesReferencesQuery,
  useGetApiVersionsArticleByIdQuery,
  ArticleReference,
  usePutApiMarksMutation,
  usePutApiSourceContentAreasMutation,
  useGetApiVersionsInvalidMarksQuery,
} from '../../../redux/store/api/api';
import useGetCategoryByArticleId from '../../../hooks/useGetCategoryByArticleId';
import {
  EDIT_MODE_KEY,
  MARK_VISIBILITY_GROUP_KEY,
  articleIdUrlParam,
  searchKeywordUrlParam,
} from '../../../shared/constants';
import { RightKey } from '../../../shared/enums';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { selectActiveVersion } from '../../../redux/store/content/slice';
import { addMessage } from '../../../redux/store/layout/slice';
import makeContent from '../functions/makeContent';
import {
  getContentAreaFromArticleReference,
  getContentAreaFromNote,
  getSpecificTypesFromContentAreas,
} from '../functions';
import getMigratedAndAutoUpdatedContentAreas from '../functions/contentAreaMigrationAndAutoUpdate';
import useMakeSearchContentAreas from './useMakeSearchContentAreas';
import { VisibilityGroupString } from '../../notes/types';

type ContentCalculationResult = {
  content: string;
  isProcessing: boolean;
};

/**
 * Gathers all the data used for creating the version content and processes it.
 * From the data, it creates multiple content variants, of which only one is needed at a time.
 * Despite this, all variants are generated to ensure that all migrations, updates, and checks are run on all elements of one version.
 * (Except for the search highlights, as they are not saved.)
 *
 * STEPS:
 * - Migration and automatic update of all Mark and SourceContentArea (Link) objects {@link getMigratedAndAutoUpdatedContentAreas}
 *    - Migration: Adding precedingCharacters, subsequentCharacters, startNodeContainerId if missing {@link getMigratedContentAreas}
 *    - Auto update: If the range is not found where expected and the phrase, along with at least precedingCharacters or subsequentCharacters, is found {@link getAutoUpdatedContentAreas}
 *      Update the range accordingly exactly once in the content update.
 *    - Handles update count ({@link linkUpdateCount}, {@link markUpdateCount}) and processed states ({@link linksProcessing}, {@link marksProcessing}):
 *       - Update count: Since there is an automatic update of the marks from the backend
 *         (see method `HasInvalidMarks` in MarkHelper.cs)
 *         as well as from the frontend, bugs in the update function {@link getAutoUpdatedContentAreas} might cause an infinite loop.
 *         To avoid this, there is an update count that prevents further updates once the maximum number of updates is exceeded.
 *       - Processing state: Ensures the content is only displayed once processing has finished.
 * - Unify different content elements: convert to ContentArea objects
 *    - From search words {@link searchContentAreas}
 *    - Mark objects and {@link userMarkContentAreas}, {@link userGroupMarkContentAreas}, {@link generalMarkContentAreas}
 *    - SourceContentArea objects {@link linkContentAreas}
 * - Make different content variations using ContentArea objects {@link makeContent}
 * - Pick the appropriate content variant.
 *
 * @returns {ContentCalculationResult} The content variant that should be displayed and a boolean indicating whether the calculation process is finished.
 */
function useGetVersionContent(): ContentCalculationResult {
  const { t: translation } = useTranslation();
  const dispatch = useAppDispatch();
  const [linksProcessing, setLinksProcessing] = useState(true);
  const [marksProcessing, setMarksProcessing] = useState(true);
  const [linkUpdateCount, setLinkUpdateCount] = useState(0);
  const [markUpdateCount, setMarkUpdateCount] = useState(0);
  const maxRenderCount = 3;
  const [searchParams] = useSearchParams();
  const version = useAppSelector(selectActiveVersion);
  const articleId: string = searchParams.get(articleIdUrlParam) || '';
  const category = useGetCategoryByArticleId(articleId);
  const userCanEditArticle =
    category?.permittedActions?.includes(
      RightKey.RightArticleManagementEditArticle,
    ) || false;
  const [editModeIsActive] = useLocalStorage<boolean>(EDIT_MODE_KEY, false);
  const [activeMarkVisibilityGroup] =
    useSessionStorage<VisibilityGroupString | null>(
      MARK_VISIBILITY_GROUP_KEY,
      null,
    );
  const isSearchcontent = useMemo(
    () => searchParams.get(searchKeywordUrlParam) !== null,
    [searchParams],
  );

  const { isFetching: invalidMarksFetching } =
    useGetApiVersionsInvalidMarksQuery();

  const { isFetching: fetchingVersions } = useGetApiVersionsArticleByIdQuery(
    articleId
      ? { id: articleId, editMode: editModeIsActive && userCanEditArticle }
      : skipToken,
  );

  const {
    data: noteData,
    isFetching: fetchingNotes,
    refetch: refetchNotes,
  } = useGetApiNotesByVersionIdQuery(
    version.id
      ? {
          versionId: version.id,
        }
      : skipToken,
    { refetchOnMountOrArgChange: true },
  );

  const {
    data: referenceData,
    isFetching: fetchingReferences,
    refetch: refetchReferences,
  } = useGetApiArticlesReferencesQuery(
    articleId && version.isRecentVersion && version.id
      ? {
          articleId,
          versionId: version.id,
        }
      : skipToken,
    { refetchOnMountOrArgChange: true },
  );

  const references = useMemo(
    () =>
      version.isRecentVersion
        ? (referenceData?.resultObject?.articleReferenceLists?.flatMap(
            (rL) => rL.articleReferences,
          ) as ArticleReference[])
        : [],
    [referenceData, version.isRecentVersion],
  );

  const [updateAreas, { isError: updateAreaIsError, error: updateAreaError }] =
    usePutApiSourceContentAreasMutation();

  const [
    updateMarks,
    { isError: updateMarksIsError, error: updateMarksError },
  ] = usePutApiMarksMutation();

  useEffect(() => {
    if (updateAreaIsError) {
      dispatch(
        addMessage({
          id: 'UpdateMarkError',
          variant: 'danger',
          messageKeyBody:
            updateAreaError && 'data' in updateAreaError
              ? updateAreaError.data?.messageKey
              : 'unknownError',
        }),
      );
    }
  }, [updateAreaIsError]);

  useEffect(() => {
    if (updateMarksIsError) {
      dispatch(
        addMessage({
          id: 'UpdateMarkError',
          variant: 'danger',
          messageKeyBody:
            updateMarksError && 'data' in updateMarksError
              ? updateMarksError.data?.messageKey
              : 'unknownError',
        }),
      );
    }
  }, [updateMarksIsError]);

  useEffect(() => {
    if (
      fetchingVersions ||
      fetchingNotes ||
      invalidMarksFetching ||
      markUpdateCount >= maxRenderCount
    ) {
      return;
    }
    const migratedAndUpdatedAreas = _.flatten(
      getMigratedAndAutoUpdatedContentAreas(
        [
          noteData?.resultObject?.userNotes
            ?.filter((n) => n.mark)
            .map((n) => getContentAreaFromNote({ note: n })) ?? [],
          noteData?.resultObject?.userGroupNotes
            ?.filter((n) => n.mark)
            .map((n) => getContentAreaFromNote({ note: n })) ?? [],
          noteData?.resultObject?.generalNotes
            ?.filter((n) => n.mark)
            .map((n) => getContentAreaFromNote({ note: n })) ?? [],
        ],
        version,
      ),
    );
    const areasToUpdate = migratedAndUpdatedAreas.filter((a) => a.changed);

    if (
      areasToUpdate.length > 0 &&
      !!areasToUpdate[0]?.versionId &&
      version.id === areasToUpdate[0].versionId
    ) {
      updateMarks({
        body: getSpecificTypesFromContentAreas(areasToUpdate),
      }).then(() => {
        refetchNotes();
      });
      setMarkUpdateCount((prev) => prev + 1);
    }

    setMarksProcessing(false);
  }, [
    noteData,
    version,
    fetchingVersions,
    fetchingNotes,
    invalidMarksFetching,
  ]);

  // migrate and auto update sourceContentAreas
  useEffect(() => {
    if (
      fetchingVersions ||
      fetchingReferences ||
      linkUpdateCount >= maxRenderCount
    ) {
      return;
    }

    if (!version.isRecentVersion) {
      setLinksProcessing(false);
      return;
    }

    const migratedAndUpdatedAreas = _.flatten(
      getMigratedAndAutoUpdatedContentAreas(
        [
          references
            ?.filter(
              (r) =>
                r && r.sourceContentArea?.id && r.sourceArticleId === articleId,
            )
            .map((r) => getContentAreaFromArticleReference({ reference: r })) ??
            [],
        ],
        version,
      ),
    );
    const areasToUpdate = migratedAndUpdatedAreas.filter((a) => a.changed);

    if (areasToUpdate.length > 0) {
      updateAreas({
        body: getSpecificTypesFromContentAreas(areasToUpdate),
      }).then(() => {
        refetchReferences();
      });
      setLinkUpdateCount((prev) => prev + 1);
    }

    setLinksProcessing(false);
  }, [references, version, fetchingVersions, fetchingReferences]);

  useEffect(() => {
    setLinkUpdateCount(0);
    setMarkUpdateCount(0);
  }, [version]);

  // make content areas (from search words, references and marks)
  const searchContentAreas = useMakeSearchContentAreas();
  const linkContentAreas = useMemo(
    () =>
      version.isRecentVersion
        ? references
            ?.filter((r) => r && r.sourceContentArea?.id)
            .map((r) =>
              getContentAreaFromArticleReference({
                reference: r,
                iconClassName: editModeIsActive
                  ? 'icon-verlinkungen'
                  : undefined,
                description: editModeIsActive
                  ? translation('showReferenceAssociatedWithLink')
                  : undefined,
              }),
            ) ?? []
        : [],
    [version, references, editModeIsActive],
  );
  const userMarkContentAreas = useMemo(
    () =>
      noteData?.resultObject?.userNotes
        ?.filter((n) => n.mark)
        .map((n) => getContentAreaFromNote({ note: n, clickableSpan: true })) ??
      [],
    [version, noteData],
  );
  const userGroupMarkContentAreas = useMemo(
    () =>
      noteData?.resultObject?.userGroupNotes
        ?.filter((n) => n.mark)
        .map((n) => getContentAreaFromNote({ note: n, clickableSpan: true })) ??
      [],
    [noteData],
  );
  const generalMarkContentAreas = useMemo(
    () =>
      noteData?.resultObject?.generalNotes
        ?.filter((n) => n.mark)
        .map((n) => getContentAreaFromNote({ note: n, clickableSpan: true })) ??
      [],
    [noteData],
  );

  // make different content versions
  const contentWithSearchWords = useMemo(
    () =>
      makeContent({
        content: version.htmlContent || '',
        contentAreas: [...linkContentAreas, ...searchContentAreas],
      }).content,
    [linkContentAreas, searchContentAreas, version],
  );
  const contentWithLinks = useMemo(
    () =>
      makeContent({
        content: version.htmlContent || '',
        contentAreas: linkContentAreas,
      }).content,
    [linkContentAreas, version],
  );
  const contentWithUserMarks = useMemo(
    () =>
      makeContent({
        content: version.htmlContent || '',
        contentAreas: [...linkContentAreas, ...userMarkContentAreas],
      }).content,
    [linkContentAreas, userMarkContentAreas, version],
  );
  const contentWithUserGroupMarks = useMemo(
    () =>
      makeContent({
        content: version.htmlContent || '',
        contentAreas: [...linkContentAreas, ...userGroupMarkContentAreas],
      }).content,
    [linkContentAreas, userGroupMarkContentAreas, version],
  );
  const contentWithGeneralMarks = useMemo(
    () =>
      makeContent({
        content: version.htmlContent || '',
        contentAreas: [...linkContentAreas, ...generalMarkContentAreas],
      }).content,
    [linkContentAreas, generalMarkContentAreas, version],
  );

  // combine all versions to html string (one version displayed, the other versions only present in html for calculations)
  const content = useMemo(() => {
    const getContent = (c: string) => {
      const allContentVersions = [
        contentWithSearchWords,
        contentWithLinks,
        contentWithUserMarks,
        contentWithUserGroupMarks,
        contentWithGeneralMarks,
      ];

      return `${c}<div className='d-none'>${allContentVersions
        .filter((a) => a !== c)
        .join()}</div>`;
    };

    if (activeMarkVisibilityGroup === null && isSearchcontent) {
      return getContent(contentWithSearchWords);
    }
    if (activeMarkVisibilityGroup === null && !isSearchcontent) {
      return getContent(contentWithLinks);
    }
    if (activeMarkVisibilityGroup === 'User') {
      return getContent(contentWithUserMarks);
    }
    if (activeMarkVisibilityGroup === 'UserGroup') {
      return getContent(contentWithUserGroupMarks);
    }
    if (activeMarkVisibilityGroup === 'General') {
      return getContent(contentWithGeneralMarks);
    }

    return '';
  }, [
    activeMarkVisibilityGroup,
    isSearchcontent,
    contentWithSearchWords,
    contentWithLinks,
    contentWithUserMarks,
    contentWithUserGroupMarks,
    contentWithGeneralMarks,
  ]);

  return {
    content,
    isProcessing:
      version.id !== noteData?.resultObject?.versionId ||
      fetchingNotes ||
      fetchingReferences ||
      fetchingVersions ||
      linksProcessing ||
      marksProcessing,
  };
}

export default useGetVersionContent;
